import { useCallback, useMemo, useState } from "react";

export const INITIAL_HISTORY_SELECTION = {
  selectedId: null,
  scorecard: null,
  loading: false,
  error: null,
};

export default function useScorecardHistory({ service }) {
  const [scorecardHistory, setScorecardHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historySelection, setHistorySelection] = useState(INITIAL_HISTORY_SELECTION);

  const isViewingHistory = Boolean(historySelection.selectedId);

  // A period can span multiple records (one per centre), so track every
  // matching kpiDataId rather than just the first one.
  const currentPeriodKpiDataIds = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-based
    const previousMonth = currentMonth === 0 ? 12 : currentMonth;
    const previousYear = currentMonth === 0 ? now.getFullYear() - 1 : now.getFullYear();

    return new Set(
      scorecardHistory
        .filter((item) => item.month === previousMonth && item.year === previousYear)
        .map((item) => item.kpiDataId)
    );
  }, [scorecardHistory]);

  const loadScorecardHistory = useCallback(async (employeeEmail, _retried = false) => {
    setIsHistoryLoading(true);
    setHistoryError(null);

    try {
      const response = await service.loadHistory(employeeEmail);
      setScorecardHistory(Array.isArray(response) ? response : []);
    } catch {
      if (!_retried) {
        await new Promise((r) => setTimeout(r, 1500));
        return loadScorecardHistory(employeeEmail, true);
      }
      setHistoryError("Unable to load scorecard history right now.");
    } finally {
      setIsHistoryLoading(false);
    }
  }, [service]);

  return {
    scorecardHistory,
    setScorecardHistory,
    isHistoryLoading,
    historyError,
    setHistoryError,
    historySelection,
    setHistorySelection,
    isViewingHistory,
    currentPeriodKpiDataIds,
    loadScorecardHistory,
  };
}
